import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, batch } from "react-redux";
import {
  Container,
  LeftSection,
  Loader,
  PlayerLeftFrame,
  Preview,
  PreviewContainer,
  RightSection,
} from "./shorts-components";
import Workspace from "../workspace/workspace";
import Spinner, { SPINNER_TYPE } from "../../common-components/Spinner";
import { setAnimoPlayerId, setShortContainerId, setShortsPlayer, setPlayAll, getPresetItems, playPausePlayer } from "../../redux/actions/appUtils";
import ActionTypes from "../../constants/action-types";
import { getSubtitlesByDuration, getNewProject, getShortsPlayerDimensions } from "./shorts-helper";
import ShortsPlayerControlPanel from "./shorts-player-control";
import ShortsContent from "./shorts-content";
import { getWorkspaceItemSource } from "../../helper/URLHelper";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import ShortsCardWatermark from "./shorts-card-watermark";
import { PLAN } from "../../constants";

const { GET_ACTIVE_PLAYER } = contextActionType;

const ShortsPlayer = ({
  projectDetails,
  userDetails,
  shortData,
  inModal = false,
  showPricingModal,
  feedbackType,
  onLike,
  onDislike,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const appReducer = useSelector((state) => state.app);
  const t1 = useSelector((state) => state.app.get("t1"));
  const initStages = appReducer.get("initStages");
  const animoPlayerId = useMemo(() => `animo-player-${shortData?.get("id")}`, [shortData]);
  const shortContainerId = useMemo(() => `shorts-preview-${shortData?.get("id")}`, [shortData]);
  const videoElRef = useRef();
  const { getShortsContext, setShortsContext } = useShortsContext();
  const activePlayerId = getShortsContext(GET_ACTIVE_PLAYER);
  const [subtitlesInRange, setSubtitlesInRange] = useState([]);
  const [videoSrc, setVideoSrc] = useState("");
  const [timelineDuration, setTimelineDuration] = useState(0);
  const { dimensionName } = useSelector((state) => state.userDetails);

  const { SET_ACTIVE_PLAYER } = contextActionType;

  useEffect(() => {
    if (activePlayerId && activePlayerId !== animoPlayerId) {
      // Pause this player if another player is active
      dispatch(setPlayAll(false, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayerId, animoPlayerId]);

  const pausePlayer = useCallback(() => {
    const start = shortData.get("start");
    let playhead = start;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    dispatch(setPlayAll(false, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
  }, [dispatch, shortData, t1, setShortsContext]);


  const getTime = useCallback((short) => {
    const currentShort = short.toJS();
    const { start, end, clips } = currentShort;
    if (clips && clips.length > 0) {
      const startTime = clips[0].startTime || start;
      const endTime = clips[clips.length - 1].endTime || end;
      return {
        start: startTime,
        end: endTime
      };
    }
    return {
      start,
      end
    };
  }, []);
  const { start, end } = getTime(shortData);

  useEffect(() => {
    if (location.pathname.includes('shorts')) {
      dispatch(getPresetItems());
    }
  }, [dispatch, location]);

  useEffect(() => {
    if (userDetails) {
      const newProject = getNewProject(projectDetails.toJS(), shortData.toJS());
      setTimelineDuration(newProject.duration);
      const subtitles = getSubtitlesByDuration(
        start,
        end,
        projectDetails.get("localSubtitle").toJS()
      );
      setSubtitlesInRange(subtitles);
      const item = newProject.workspaceItems[Object.keys(newProject.workspaceItems)[0]];
      const { src } = getWorkspaceItemSource({
        item: { src: item.src, subType: item.subType, type: item.type },
      });
      setVideoSrc(src);

      batch(() => {
        dispatch(setAnimoPlayerId(animoPlayerId));
        dispatch(setShortsPlayer({ isShortsPlayer: true }));
        dispatch(setShortContainerId(shortContainerId));
        dispatch({ type: ActionTypes.SET_USER_DATA, payload: { ...userDetails } });
        dispatch({ type: ActionTypes.SET_PROJECT, payload: newProject });
        if (src) {
          dispatch(setPlayAll(true, false));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails, userDetails, shortData]);

  let workspaceComponent = null;
  if (initStages.get("projectLoaded")) {
    workspaceComponent = <Workspace />;
  }

  const timeUpdateHandler = (e) => {
    const { currentTime } = e.target;
    if (currentTime >= end) {
      videoElRef.current.pause();
      videoElRef.current.currentTime = start;
      videoElRef.current.style.display = "none";
    }
  };

  const item = projectDetails?.getIn(["workspaceItems"])?.first();

  const { videoStyles, containerStyles } = useMemo(() => {
    if (!item || !projectDetails) {
      return { videoStyles: {}, containerStyles: {} };
    }

    const projectWidth = projectDetails.get("width");
    const projectHeight = projectDetails.get("height");
    const { effectiveZoom, containerWidth, containerHeight } = getShortsPlayerDimensions(
      dimensionName,
      projectWidth,
      projectHeight
    );

    return {
      videoStyles: {
        width: `${item.get("width") * effectiveZoom}px`,
        height: `${item.get("height") * effectiveZoom}px`,
        top: `${item.get("y") * effectiveZoom}px`,
        left: `${item.get("x") * effectiveZoom}px`,
      },
      containerStyles: {
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      },
    };
  }, [item, projectDetails, dimensionName]);

  const showPlayerWatermark = userDetails?.plan === PLAN.LITE;

  const handleWatermarkClick = useCallback((e) => {
    e.stopPropagation();
    pausePlayer();
    showPricingModal?.({ type: "longtoshort", source: "shorts-player-watermark" });
  }, [showPricingModal]);

  return (
    <Container isVertical={dimensionName === "vertical"} $inModal={inModal}>
      <PlayerLeftFrame style={{ width: containerStyles.width }}>
        <LeftSection
          id={animoPlayerId}
          style={containerStyles}
          $hasWatermarkBar={showPlayerWatermark}
        >
          {workspaceComponent || (
            <Loader>
              <Spinner type={SPINNER_TYPE.RING_SPINNER} spinnerHeight={"130px"} />
            </Loader>
          )}
          <PreviewContainer id={shortContainerId}>
            <Preview
              ref={videoElRef}
              $height={videoStyles.height}
              $width={videoStyles.width}
              $left={videoStyles.left}
              $top={videoStyles.top}
              src={videoSrc}
              preload="auto"
              muted
              onLoadedMetadata={(e) => {
                e.target.currentTime = start || 0;
              }}
              onTimeUpdate={timeUpdateHandler}
            />
          </PreviewContainer>
          <ShortsPlayerControlPanel
            startTime={0}
            endTime={timelineDuration}
            videoRef={videoElRef}
            isShorts={location.pathname.includes('shorts')}
          />
        </LeftSection>
        {showPlayerWatermark && (
          <ShortsCardWatermark onClick={handleWatermarkClick} isVertical={dimensionName === "vertical"} />
        )}
      </PlayerLeftFrame>
      <RightSection $dimensionName={dimensionName} $inModal={inModal}>
        <ShortsContent
          shortData={shortData}
          subtitlesInRange={subtitlesInRange}
          inModal={inModal}
          showPricingModal={showPricingModal}
          feedbackType={feedbackType}
          onLike={onLike}
          onDislike={onDislike}
        />
      </RightSection>
    </Container>
  );
};

ShortsPlayer.propTypes = {
  projectDetails: PropTypes.object,
  userDetails: PropTypes.object,
  shortData: PropTypes.object,
  inModal: PropTypes.bool,
  showPricingModal: PropTypes.func,
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
};

export default React.memo(ShortsPlayer, (prevProps, nextProps) => {
  return (
    prevProps.shortData === nextProps.shortData
    && prevProps.userDetails === nextProps.userDetails
    && prevProps.projectDetails.get("workspaceItems") === nextProps.projectDetails.get("workspaceItems")
    && prevProps.projectDetails.get("localSubtitle") === nextProps.projectDetails.get("localSubtitle")
    && prevProps.feedbackType === nextProps.feedbackType
  );
});
